import { Button, Form, Input, Typography } from 'antd';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { extractErrorMessage } from '@shared/api';
import { useConfirmPasswordResetMutation } from '../api/mutations';

const { Title, Text } = Typography;

interface ResetFormValues {
  code: string;
  new_password: string;
  passwordConfirm: string;
}

interface LocationState {
  email?: string;
}

export function ResetPasswordForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const state = (location.state ?? {}) as LocationState;
  const email = state.email;

  const [form] = Form.useForm<ResetFormValues>();
  const mutation = useConfirmPasswordResetMutation();

  if (!email) {
    return (
      <div>
        <Title level={3} style={{ marginTop: 0 }}>
          Восстановление пароля
        </Title>
        <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
          Сначала запросите код.
        </Text>
        <Button type="primary" size="large" block onClick={() => navigate('/forgot-password')}>
          Запросить код
        </Button>
      </div>
    );
  }

  const handleSubmit = async (values: ResetFormValues) => {
    try {
      await mutation.mutateAsync({
        email,
        code: values.code,
        new_password: values.new_password,
      });
      navigate('/login', { state: { passwordResetSuccess: true } });
    } catch {
      // см. error
    }
  };

  const errorMessage = mutation.error ? extractErrorMessage(mutation.error) : null;

  return (
    <div>
      <Title level={3} style={{ marginTop: 0, marginBottom: 4 }}>
        Новый пароль
      </Title>
      <Text type="secondary" style={{ display: 'block', marginBottom: 24 }}>
        Код отправлен на <b style={{ color: '#1A1A1A' }}>{email}</b>. Введите его и новый пароль.
      </Text>

      <Form<ResetFormValues>
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        requiredMark={false}
      >
        <Form.Item
          name="code"
          label="Код из письма"
          rules={[
            { required: true, message: 'Введите код' },
            { len: 6, message: 'Код состоит из 6 цифр' },
            { pattern: /^\d{6}$/, message: 'Только цифры' },
          ]}
        >
          <Input
            size="large"
            placeholder="000000"
            maxLength={6}
            autoFocus
            inputMode="numeric"
            style={{ letterSpacing: 4, fontSize: 18, textAlign: 'center' }}
          />
        </Form.Item>

        <Form.Item
          name="new_password"
          label="Новый пароль"
          rules={[
            { required: true, message: 'Придумайте пароль' },
            { min: 8, message: 'Минимум 8 символов' },
            { max: 128, message: 'Максимум 128 символов' },
          ]}
        >
          <Input.Password size="large" placeholder="••••••••" autoComplete="new-password" />
        </Form.Item>

        <Form.Item
          name="passwordConfirm"
          label="Повторите пароль"
          dependencies={['new_password']}
          rules={[
            { required: true, message: 'Повторите пароль' },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue('new_password') === value) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error('Пароли не совпадают'));
              },
            }),
          ]}
        >
          <Input.Password size="large" placeholder="••••••••" autoComplete="new-password" />
        </Form.Item>

        {errorMessage && (
          <div style={{ marginBottom: 16, color: '#DC2626', fontSize: 13 }}>{errorMessage}</div>
        )}

        <Button type="primary" htmlType="submit" size="large" block loading={mutation.isPending}>
          Сменить пароль
        </Button>

        <div style={{ marginTop: 16, textAlign: 'center' }}>
          <Link to="/login">Назад ко входу</Link>
        </div>
      </Form>
    </div>
  );
}
